import { useMemo } from "react";
import PropTypes from "prop-types";

import { Country } from "@constants/Country";

import MenuContainer from "@components/header/desktopMenu/menu/MenuContainer";
import { Link } from "@components/al_components/ALLink";

import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";

import { useCurrentCountryCode } from "@hooks/usePrices";

import Customizable from "@images/custom.png";

import "@components/header/desktopMenu/menu/styles.scss";

function MenuTitleText({ menuCategories, children, ...props }) {
  const isSingleMenuItem = menuCategories?.length === 1 && menuCategories[0].links.length === 1;
  if (isSingleMenuItem) {
    const [item] = menuCategories[0].links;
    const destinationUrl = item.title === "empty" ? menuCategories[0].titleLink : item.link.link;
    return (
      <Link className={props.className} style={props.style} to={destinationUrl}>
        {children}
      </Link>
    );
  }
  return <button {...props}>{children}</button>;
}

function Menu({ menu, showMenu, openMenuId, setOpenMenuId }) {
  const { title: menuTitle, textColor: color, menuContainer } = menu;
  const { id, menuCategories, imageLinks } = menuContainer[0];
  const isSingleMenuItem = menuCategories?.length === 1 && menuCategories[0].links.length === 1;
  const isExpanded = openMenuId === menu.id && !isSingleMenuItem;
  const countryCode = useCurrentCountryCode();
  const isUSCountry = countryCode === Country.US;

  // Filter out AL Rewards for US and AL Luxe for non-US
  const filteredImageLinks = useMemo(
    () =>
      imageLinks?.filter(
        (imageLink) =>
          (isUSCountry && imageLink.title !== "AL Rewards") ||
          (!isUSCountry && imageLink.title !== "AL Luxe")
      ),
    [imageLinks, isUSCountry]
  );

  const handleMouseEnterMenuItem = () => {
    showMenu();
    if (isSingleMenuItem) {
      setOpenMenuId(null);
    }
  };

  return (
    <div className="header_left_menu_item__container" onMouseEnter={handleMouseEnterMenuItem}>
      <MenuTitleText
        menuCategories={menuCategories}
        className={`header_left_menu_item menu-link-underline pt-[6px] header_left_menu_item_${menuTitle
          .replace(" ", "")
          .toLowerCase()}`}
        aria-expanded={isExpanded}
        style={{ color }}
        type="button"
        onMouseEnter={() => setOpenMenuId(isSingleMenuItem ? null : menu.id)}
        onFocus={() => setOpenMenuId(isSingleMenuItem ? null : menu.id)}
      >
        <h3
          className="typo-micro font-bold flex items-center gap-[4px] mobile:gap-[3px]"
          style={{ color }}
        >
          {menuTitle === "Custom" && (
            <img
              className="w-[11px] h-[11px] mobile:w-[10px] mobile:h-[10px]"
              src={Customizable}
              alt="Customizable"
              loading="lazy"
            />
          )}
          {menuTitle}
        </h3>
      </MenuTitleText>
      <div
        className={cn(
          "header_left_menu_content navMenu__containers",
          filteredImageLinks && filteredImageLinks.length <= 3 && "header_left_menu_content__short",
          "py-[40px] px-[1.74vw]"
        )}
        key={menu.id}
        aria-expanded={isExpanded}
        role="menuitem"
        onMouseEnter={() => !isSingleMenuItem && setOpenMenuId(menu.id)}
        onFocus={() => !isSingleMenuItem && setOpenMenuId(menu.id)}
        onBlur={() => setOpenMenuId(null)}
        tabIndex="-1"
      >
        <MenuContainer
          key={id}
          categories={menuCategories}
          imageLinks={filteredImageLinks}
          menuTitle={menuTitle}
          closeMenu={() => setOpenMenuId(null)}
        />
      </div>
    </div>
  );
}

Menu.propTypes = {
  showMenu: PropTypes.func,
  openMenuId: PropTypes.string,
  setOpenMenuId: PropTypes.func,
  menu: PropTypes.shape({
    id: PropTypes.string,
    textColor: PropTypes.string,
    title: PropTypes.string,
    menuContainer: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        imageLinks: PropTypes.array,
        menuCategories: PropTypes.array,
      })
    ),
  }),
};

export default withALErrorBoundary({
  name: "Menu",
  priority: "P2",
})(Menu);
